import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  Palette,
  User,
  Upload,
  Layout,
  MousePointer2,
  X,
  Check,
} from 'lucide-react';
import { useDialog } from '@/context/DialogContext';
import { toast } from 'sonner';
import axios, { API } from '@/utils/api';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// ==================== EMAIL BUTTON EXTENSION ====================
// Custom Tiptap node that renders buttons visually in the editor

// Button presets (moved here for use in extension)
const BUTTON_PRESETS = {
  primary: { bg: 'linear-gradient(90deg, #df1283, #FF7901)', color: '#ffffff', border: 'none' },
  orange: { bg: '#FF7901', color: '#ffffff', border: 'none' },
  purple: { bg: '#4A0A66', color: '#ffffff', border: 'none' },
  teal: { bg: '#0FFFE2', color: '#000000', border: 'none' },
  outline: { bg: 'transparent', color: '#df1283', border: '2px solid #df1283' },
  dark: { bg: '#1a1a1a', color: '#ffffff', border: 'none' },
};

const PADDING_PRESETS = {
  compact: '10px 20px',
  normal: '14px 28px',
  large: '18px 36px',
};

// Helper to check if color is light
const isLightColor = (hex) => {
  if (!hex || hex === 'transparent') return false;
  const c = hex.replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

// React component to render the button in the editor
const EmailButtonComponent = ({ node, updateAttributes, selected }) => {
  const { text, url, style, customColor, useCustomColor, alignment, padding } = node.attrs;
  
  const preset = BUTTON_PRESETS[style] || BUTTON_PRESETS.primary;
  const bg = useCustomColor ? customColor : preset.bg;
  const color = useCustomColor 
    ? (isLightColor(customColor) ? '#000000' : '#ffffff')
    : preset.color;
  const border = useCustomColor ? 'none' : preset.border;
  const paddingValue = PADDING_PRESETS[padding] || PADDING_PRESETS.normal;
  
  return (
    <NodeViewWrapper 
      className="email-button-wrapper"
      style={{ 
        textAlign: alignment || 'center',
        margin: '16px 0',
      }}
    >
      <a
        href={url}
        onClick={(e) => e.preventDefault()}
        className={`email-button-node ${selected ? 'selected' : ''}`}
        style={{
          background: bg,
          color: color,
          padding: paddingValue,
          textDecoration: 'none',
          borderRadius: '8px',
          fontWeight: 'bold',
          display: 'inline-block',
          border: border !== 'none' ? border : undefined,
          cursor: 'pointer',
          outline: selected ? '2px solid #df1283' : 'none',
          outlineOffset: '2px',
        }}
      >
        {text || 'Button'}
      </a>
    </NodeViewWrapper>
  );
};

// Custom Tiptap Extension for Email Buttons
const EmailButton = Node.create({
  name: 'emailButton',
  group: 'block',
  atom: true,
  draggable: true,
  
  addAttributes() {
    return {
      text: { default: 'Click Here' },
      url: { default: 'https://' },
      style: { default: 'primary' },
      customColor: { default: '#df1283' },
      useCustomColor: { default: false },
      alignment: { default: 'center' },
      padding: { default: 'normal' },
    };
  },
  
  parseHTML() {
    return [
      {
        tag: 'div[data-email-button]',
      },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    // Generate inline-styled HTML for email output
    const { text, url, style, customColor, useCustomColor, alignment, padding } = HTMLAttributes;
    
    const preset = BUTTON_PRESETS[style] || BUTTON_PRESETS.primary;
    const bg = useCustomColor ? customColor : preset.bg;
    const color = useCustomColor 
      ? (isLightColor(customColor) ? '#000000' : '#ffffff')
      : preset.color;
    const border = useCustomColor ? '' : (preset.border !== 'none' ? `border: ${preset.border};` : '');
    const paddingValue = PADDING_PRESETS[padding] || PADDING_PRESETS.normal;
    
    const buttonStyle = `background: ${bg}; color: ${color}; padding: ${paddingValue}; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;${border}`;
    
    return [
      'p',
      { style: `text-align: ${alignment || 'center'}; margin: 20px 0;` },
      ['a', { href: url, style: buttonStyle }, text || 'Button']
    ];
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(EmailButtonComponent);
  },
});

// Template variables that can be inserted
const TEMPLATE_VARIABLES = [
  { label: 'First Name', value: '{{first_name}}', description: 'User\'s first name' },
  { label: 'Email', value: '{{email}}', description: 'User\'s email address' },
  { label: 'App Name', value: 'UnCloned® Journal', description: 'Application name' },
];

// Pre-designed email templates
const EMAIL_TEMPLATES = [
  {
    name: 'Simple Announcement',
    content: `<p>Hi {{first_name}},</p>
<p>We have some exciting news to share with you!</p>
<p>[Your message here]</p>
<p>Best regards,<br/>The UnCloned® Journal Team</p>`
  },
  {
    name: 'Feature Update',
    content: `<h2 style="color: #df1283;">New Feature Alert!</h2>
<p>Hi {{first_name}},</p>
<p>We've just launched something amazing that we think you'll love.</p>
<p><strong>What's New:</strong></p>
<ul>
<li>Feature 1</li>
<li>Feature 2</li>
<li>Feature 3</li>
</ul>
<p>Log in now to try it out!</p>
<p>Happy journaling,<br/>The UnCloned® Team</p>`
  },
  {
    name: 'Welcome Email',
    content: `<h1 style="color: #df1283; text-align: center;">Welcome to UnCloned®!</h1>
<p style="text-align: center;">Hi {{first_name}},</p>
<p style="text-align: center;">Thank you for joining the UnCloned® Journal community.</p>
<p style="text-align: center;">Your journey to mindful journaling starts here.</p>
<p style="text-align: center;"><strong>Get Started:</strong></p>
<ul>
<li>Write your first journal entry</li>
<li>Try voice typing with AI transcription</li>
<li>Get personalized AI reflections</li>
</ul>
<p style="text-align: center;">Happy journaling!<br/>The UnCloned® Team</p>`
  },
  {
    name: 'Promotional',
    content: `<div style="text-align: center; padding: 20px;">
<h1 style="color: #FF7901;">Special Offer Inside!</h1>
<p>Hi {{first_name}},</p>
<p style="font-size: 18px;">Don't miss out on this exclusive opportunity.</p>
<p>[Add your promotional content here]</p>
<p style="margin-top: 20px;">
<a href="#" style="background: linear-gradient(90deg, #df1283, #FF7901); color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; font-weight: bold;">Claim Now</a>
</p>
</div>`
  }
];

// Color presets for email
const COLOR_PRESETS = [
  { name: 'Pink', value: '#df1283' },
  { name: 'Orange', value: '#FF7901' },
  { name: 'Teal', value: '#0FFFE2' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Black', value: '#000000' },
  { name: 'Gray', value: '#666666' },
];

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded-lg transition-all duration-200 ${
      isActive
        ? 'bg-[#FF7901] text-white'
        : 'text-[#FDF2F8]/70 hover:text-white hover:bg-[#4A0A66]/50'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const DropdownButton = ({ icon: Icon, title, children, className = '', alignRight = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={title}
        className="p-2 rounded-lg transition-all duration-200 text-[#FDF2F8]/70 hover:text-white hover:bg-[#4A0A66]/50 flex items-center gap-1"
      >
        <Icon className="w-4 h-4" />
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className={`absolute top-full mt-1 bg-[#2D063E] border border-[#4A0A66] rounded-lg shadow-xl z-[9999] min-w-[180px] max-h-[300px] overflow-y-auto ${alignRight ? 'right-0' : 'left-0'}`}>
          {children}
        </div>
      )}
    </div>
  );
};

const EmailEditor = ({ value, onChange, placeholder, onImageUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [buttonConfig, setButtonConfig] = useState({
    text: 'Click Here',
    url: 'https://',
    style: 'primary',
    customColor: '#df1283',
    useCustomColor: false,
    alignment: 'center', // left, center, right
    padding: 'normal', // compact, normal, large
  });
  const fileInputRef = useRef(null);
  const { prompt, confirm, alert } = useDialog();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#0FFFE2] underline',
          style: 'color: #0FFFE2;',
        },
      }),
      EmailButton, // Custom email button extension
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg',
          style: 'max-width: 100%; border-radius: 8px;',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Write your email...',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[250px] p-4',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const addLink = useCallback(async () => {
    const url = await prompt({
      title: 'Add Link',
      message: 'Enter the URL:',
      placeholder: 'https://example.com',
      submitText: 'Add Link',
    });
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor, prompt]);

  const addImageUrl = useCallback(async () => {
    const url = await prompt({
      title: 'Add Image',
      message: 'Enter the image URL:',
      placeholder: 'https://example.com/image.jpg',
      submitText: 'Add Image',
    });
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, prompt]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Convert to base64 and upload
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        // Upload to backend
        const response = await fetch(`${API_BASE}/api/admin/upload-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: base64 })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.url) {
            editor.chain().focus().setImage({ src: data.url }).run();
          }
        } else {
          // Fallback: use base64 directly (not recommended for large images)
          editor.chain().focus().setImage({ src: base64 }).run();
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Image upload error:', err);
      setUploading(false);
    }
  };

  const insertVariable = (variable) => {
    editor.chain().focus().insertContent(variable).run();
  };

  const applyTemplate = async (template) => {
    const confirmed = await confirm({
      title: 'Apply Template',
      message: 'This will replace your current content. Continue?',
      confirmText: 'Apply',
      cancelText: 'Cancel',
      variant: 'default'
    });
    if (confirmed) {
      editor.commands.setContent(template.content);
      onChange(template.content);
    }
  };

  const setTextColor = (color) => {
    editor.chain().focus().setColor(color).run();
  };

  // Preset button styles
  const buttonPresets = {
    primary: { 
      name: 'Primary (Gradient)', 
      bg: 'linear-gradient(90deg, #df1283, #FF7901)',
      color: '#ffffff',
      border: 'none',
    },
    orange: { 
      name: 'Orange', 
      bg: '#FF7901',
      color: '#ffffff',
      border: 'none',
    },
    purple: { 
      name: 'Purple', 
      bg: '#4A0A66',
      color: '#ffffff',
      border: 'none',
    },
    teal: { 
      name: 'Teal', 
      bg: '#0FFFE2',
      color: '#000000',
      border: 'none',
    },
    outline: { 
      name: 'Outline', 
      bg: 'transparent',
      color: '#df1283',
      border: '2px solid #df1283',
    },
    dark: { 
      name: 'Dark', 
      bg: '#1a1a1a',
      color: '#ffffff',
      border: 'none',
    },
  };

  // Padding presets
  const paddingPresets = {
    compact: { padding: '10px 20px', label: 'Compact' },
    normal: { padding: '14px 28px', label: 'Normal' },
    large: { padding: '18px 36px', label: 'Large' },
  };

  // Generate button style string based on config
  const getButtonStyleString = (config) => {
    const preset = buttonPresets[config.style];
    const bg = config.useCustomColor ? config.customColor : preset.bg;
    const color = config.useCustomColor 
      ? (isLightColor(config.customColor) ? '#000000' : '#ffffff')
      : preset.color;
    const border = config.useCustomColor ? 'none' : preset.border;
    const padding = paddingPresets[config.padding]?.padding || '14px 28px';
    
    return `background: ${bg}; color: ${color}; padding: ${padding}; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;${border !== 'none' ? ` border: ${border};` : ''}`;
  };

  // Check if a color is light (for text contrast)
  const isLightColor = (hex) => {
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  // Insert the configured button as a custom node
  const insertConfiguredButton = useCallback(() => {
    if (!buttonConfig.text.trim() || !buttonConfig.url.trim()) {
      toast.error('Please enter button text and URL');
      return;
    }
    
    // Insert the custom emailButton node
    editor.chain().focus().insertContent({
      type: 'emailButton',
      attrs: {
        text: buttonConfig.text.trim(),
        url: buttonConfig.url.trim(),
        style: buttonConfig.style,
        customColor: buttonConfig.customColor,
        useCustomColor: buttonConfig.useCustomColor,
        alignment: buttonConfig.alignment,
        padding: buttonConfig.padding,
      },
    }).run();
    
    setShowButtonModal(false);
    
    // Reset to defaults
    setButtonConfig({
      text: 'Click Here',
      url: 'https://',
      style: 'primary',
      customColor: '#df1283',
      useCustomColor: false,
      alignment: 'center',
      padding: 'normal',
    });
  }, [editor, buttonConfig]);

  if (!editor) return null;

  return (
    <div className="border border-[#4A0A66] rounded-xl bg-[#4A0A66]/20">
      {/* Toolbar Row 1 - Formatting */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-[#4A0A66]/50 bg-[#2D063E]/50 rounded-t-xl overflow-visible">
        {/* Text Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </MenuButton>
        
        <div className="w-px h-6 bg-[#4A0A66]/50 mx-1" />
        
        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>
        
        <div className="w-px h-6 bg-[#4A0A66]/50 mx-1" />
        
        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </MenuButton>
        
        <div className="w-px h-6 bg-[#4A0A66]/50 mx-1" />
        
        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>
        
        <div className="w-px h-6 bg-[#4A0A66]/50 mx-1" />
        
        {/* Link & Image */}
        <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Add Link">
          <LinkIcon className="w-4 h-4" />
        </MenuButton>
        
        {/* Image Dropdown - align right to prevent cutoff */}
        <DropdownButton icon={ImageIcon} title="Insert Image" alignRight>
          <button
            onClick={addImageUrl}
            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2 whitespace-nowrap"
          >
            <LinkIcon className="w-4 h-4 flex-shrink-0" />
            <span>From URL</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2 whitespace-nowrap"
          >
            <Upload className="w-4 h-4 flex-shrink-0" />
            <span>Upload Image</span>
          </button>
        </DropdownButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        
        {/* Button Dropdown - for CTA buttons */}
        <MenuButton 
          onClick={() => setShowButtonModal(true)} 
          title="Insert Button"
        >
          <MousePointer2 className="w-4 h-4" />
        </MenuButton>
        
        <div className="flex-1" />
        
        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>
      </div>
      
      {/* Toolbar Row 2 - Advanced Features */}
      <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-[#4A0A66]/30 bg-[#2D063E]/30">
        {/* Color Picker */}
        <DropdownButton icon={Palette} title="Text Color">
          <div className="p-2">
            <p className="text-xs text-[#FDF2F8]/50 mb-2 px-1">Text Color</p>
            <div className="grid grid-cols-3 gap-1">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setTextColor(color.value)}
                  className="w-8 h-8 rounded border border-[#4A0A66] hover:scale-110 transition-transform"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </DropdownButton>
        
        {/* Template Variables */}
        <DropdownButton icon={User} title="Insert Variable">
          <div className="py-1">
            <p className="text-xs text-[#FDF2F8]/50 px-3 py-1">Personalization</p>
            {TEMPLATE_VARIABLES.map((variable) => (
              <button
                key={variable.value}
                onClick={() => insertVariable(variable.value)}
                className="w-full px-3 py-2 text-left hover:bg-[#4A0A66]/50"
              >
                <span className="text-sm text-white font-medium">{variable.label}</span>
                <span className="text-xs text-[#FDF2F8]/50 ml-2">{variable.value}</span>
              </button>
            ))}
          </div>
        </DropdownButton>
        
        {/* Email Templates */}
        <DropdownButton icon={Layout} title="Email Templates">
          <div className="py-1">
            <p className="text-xs text-[#FDF2F8]/50 px-3 py-1">Quick Templates</p>
            {EMAIL_TEMPLATES.map((template) => (
              <button
                key={template.name}
                onClick={() => applyTemplate(template)}
                className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#4A0A66]/50"
              >
                {template.name}
              </button>
            ))}
          </div>
        </DropdownButton>
        
        {uploading && (
          <span className="text-xs text-[#FF7901] animate-pulse">Uploading image...</span>
        )}
      </div>
      
      {/* Editor Content */}
      <EditorContent editor={editor} className="text-white" />
      
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: rgba(253, 242, 248, 0.3);
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror {
          min-height: 250px;
        }
        .ProseMirror p {
          margin: 0.5em 0;
        }
        .ProseMirror h1 {
          font-size: 1.75em;
          font-weight: bold;
          margin: 0.5em 0;
          color: #df1283;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.5em 0;
          color: #FF7901;
        }
        .ProseMirror h3 {
          font-size: 1.25em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #FF7901;
          padding-left: 1em;
          margin: 0.5em 0;
          color: rgba(253, 242, 248, 0.7);
          font-style: italic;
        }
        .ProseMirror img {
          max-width: 100%;
          border-radius: 8px;
          margin: 1em 0;
        }
        .ProseMirror a {
          color: #0FFFE2;
          text-decoration: underline;
        }
        .ProseMirror p[style*="text-align: center"] {
          text-align: center;
        }
        .ProseMirror p[style*="text-align: right"] {
          text-align: right;
        }
        /* Email Button Node Styles */
        .email-button-wrapper {
          user-select: none;
        }
        .email-button-node {
          transition: all 0.15s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .email-button-node:hover {
          opacity: 0.9;
          box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        }
        .email-button-node.selected {
          box-shadow: 0 0 0 2px #df1283, 0 4px 8px rgba(0,0,0,0.3);
        }
      `}</style>
      
      {/* Button Configuration Modal */}
      {showButtonModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-lg shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
                  <MousePointer2 className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Insert Button</h3>
              </div>
              <button 
                onClick={() => setShowButtonModal(false)}
                className="p-2 text-white/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Button Text & URL */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={buttonConfig.text}
                    onChange={(e) => setButtonConfig(prev => ({ ...prev, text: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-white text-sm focus:outline-none focus:border-[#df1283]"
                    placeholder="Click Here"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Button URL</label>
                  <input
                    type="text"
                    value={buttonConfig.url}
                    onChange={(e) => setButtonConfig(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-white text-sm focus:outline-none focus:border-[#df1283]"
                    placeholder="https://example.com"
                  />
                </div>
              </div>
              
              {/* Style Selection */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Choose Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(buttonPresets).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => setButtonConfig(prev => ({ ...prev, style: key, useCustomColor: false }))}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        buttonConfig.style === key && !buttonConfig.useCustomColor
                          ? 'border-[#df1283] bg-[#4A0A66]/30' 
                          : 'border-[#4A0A66]/50 hover:border-[#4A0A66]'
                      }`}
                    >
                      <div 
                        className="h-8 rounded-md flex items-center justify-center text-xs font-semibold"
                        style={{ 
                          background: preset.bg, 
                          color: preset.color,
                          border: preset.border !== 'none' ? preset.border : 'none'
                        }}
                      >
                        Button
                      </div>
                      <p className="text-[10px] text-white/60 mt-1 text-center">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Color Option */}
              <div className="p-3 bg-[#1a0527]/50 rounded-lg border border-[#4A0A66]/30">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={buttonConfig.useCustomColor}
                    onChange={(e) => setButtonConfig(prev => ({ ...prev, useCustomColor: e.target.checked }))}
                    className="w-4 h-4 rounded border-[#4A0A66] bg-[#1a0527] text-[#df1283] focus:ring-[#df1283] focus:ring-offset-0"
                  />
                  <span className="text-sm text-white">Use custom color</span>
                </label>
                
                {buttonConfig.useCustomColor && (
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="color"
                      value={buttonConfig.customColor}
                      onChange={(e) => setButtonConfig(prev => ({ ...prev, customColor: e.target.value }))}
                      className="w-10 h-10 rounded-lg cursor-pointer border-2 border-[#4A0A66]"
                    />
                    <input
                      type="text"
                      value={buttonConfig.customColor}
                      onChange={(e) => setButtonConfig(prev => ({ ...prev, customColor: e.target.value }))}
                      className="flex-1 px-3 py-2 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-white text-sm font-mono focus:outline-none focus:border-[#df1283]"
                      placeholder="#df1283"
                    />
                  </div>
                )}
              </div>
              
              {/* Button Alignment */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Alignment</label>
                <div className="flex gap-2">
                  {[
                    { value: 'left', icon: <AlignLeft className="w-4 h-4" />, label: 'Left' },
                    { value: 'center', icon: <AlignCenter className="w-4 h-4" />, label: 'Center' },
                    { value: 'right', icon: <AlignRight className="w-4 h-4" />, label: 'Right' },
                  ].map((align) => (
                    <button
                      key={align.value}
                      onClick={() => setButtonConfig(prev => ({ ...prev, alignment: align.value }))}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                        buttonConfig.alignment === align.value
                          ? 'border-[#df1283] bg-[#4A0A66]/30 text-white'
                          : 'border-[#4A0A66]/50 text-white/60 hover:border-[#4A0A66] hover:text-white'
                      }`}
                    >
                      {align.icon}
                      <span className="text-sm">{align.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Button Padding */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Button Size</label>
                <div className="flex gap-2">
                  {[
                    { value: 'compact', label: 'Compact', desc: 'Smaller padding' },
                    { value: 'normal', label: 'Normal', desc: 'Default size' },
                    { value: 'large', label: 'Large', desc: 'More padding' },
                  ].map((size) => (
                    <button
                      key={size.value}
                      onClick={() => setButtonConfig(prev => ({ ...prev, padding: size.value }))}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all ${
                        buttonConfig.padding === size.value
                          ? 'border-[#df1283] bg-[#4A0A66]/30 text-white'
                          : 'border-[#4A0A66]/50 text-white/60 hover:border-[#4A0A66] hover:text-white'
                      }`}
                    >
                      <span className="text-sm font-medium">{size.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Live Preview */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Live Preview</label>
                <div 
                  className="p-6 bg-white rounded-lg"
                  style={{ 
                    display: 'flex', 
                    justifyContent: buttonConfig.alignment === 'left' ? 'flex-start' : buttonConfig.alignment === 'right' ? 'flex-end' : 'center' 
                  }}
                >
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    style={{
                      ...(buttonConfig.useCustomColor 
                        ? {
                            background: buttonConfig.customColor,
                            color: isLightColor(buttonConfig.customColor) ? '#000000' : '#ffffff',
                          }
                        : {
                            background: buttonPresets[buttonConfig.style].bg,
                            color: buttonPresets[buttonConfig.style].color,
                            border: buttonPresets[buttonConfig.style].border !== 'none' 
                              ? buttonPresets[buttonConfig.style].border 
                              : undefined,
                          }
                      ),
                      padding: paddingPresets[buttonConfig.padding]?.padding || '14px 28px',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      display: 'inline-block',
                    }}
                  >
                    {buttonConfig.text || 'Button Text'}
                  </a>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-[#4A0A66]/50">
              <button
                onClick={() => setShowButtonModal(false)}
                className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={insertConfiguredButton}
                className="px-4 py-2 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Insert Button
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailEditor;
